import { useEffect, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "../lib/api";
import { CATEGORIES } from "../lib/site";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { resizeImage } from "../lib/image";

const EMPTY = { name: "", category: "buckets", pack_size: "", price: "", unit: "per unit", description: "", image: "", featured: false, in_stock: true, sort_order: 0 };


export default function ProductDialog({ product, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) setF({ ...EMPTY, ...product, price: product.price ?? "" });
  }, [product]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (file) set("image", await resizeImage(file));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { id, ...rest } = f;
    const body = { ...rest, price: f.price === "" ? null : Number(f.price), sort_order: Number(f.sort_order) || 0 };
    try {
      if (product?.id) await api.put(`/products/${product.id}`, body);
      else await api.post("/products", body);
      toast.success("Product saved");
      onSaved();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const input = (k, label, props = {}) => (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-navy">{label}</span>
      <input data-testid={`product-form-${k}`} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="field" {...props} />
    </label>
  );

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-product-dialog" className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>{product?.id ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">{input("name", "Product name", { required: true })}</div>
          <label className="block">
            <span className="mb-1.5 block text-sm font-semibold text-navy">Category</span>
            <select data-testid="product-form-category" value={f.category} onChange={(e) => set("category", e.target.value)} className="field">
              {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </label>
          {input("pack_size", "Pack size", { placeholder: "e.g. 20 L Bucket" })}
          {input("price", "Price (₹) — leave blank for 'on request'", { type: "number", min: 0, step: "0.01" })}
          {input("unit", "Price unit", { placeholder: "e.g. per can" })}
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-sm font-semibold text-navy">Description</span>
            <textarea data-testid="product-form-description" rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} className="field" />
          </label>
          <div className="sm:col-span-2 flex items-center gap-4">
            <img src={f.image || "/brand/mhm-logo.jpg"} alt="" className="h-20 w-20 rounded-xl object-contain bg-slate-100" />
            <div className="flex-1 space-y-2">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-navy/15 px-4 py-2 text-sm font-semibold text-navy hover:border-brand">
                <Upload className="h-4 w-4" /> Upload image
                <input data-testid="product-form-image-upload" type="file" accept="image/*" onChange={onFile} className="hidden" />
              </label>
              <input data-testid="product-form-image-url" value={f.image.startsWith("data:") ? "" : f.image} onChange={(e) => set("image", e.target.value)} placeholder="…or paste an image URL" className="field !py-2 text-sm" />
            </div>
          </div>
          {input("sort_order", "Display order", { type: "number" })}
          <div className="flex items-end gap-6 pb-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-navy"><Switch data-testid="product-form-featured" checked={f.featured} onCheckedChange={(v) => set("featured", v)} /> Featured</label>
            <label className="flex items-center gap-2 text-sm font-semibold text-navy"><Switch data-testid="product-form-instock" checked={f.in_stock} onCheckedChange={(v) => set("in_stock", v)} /> In stock</label>
          </div>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-semibold text-navy hover:bg-slate-100">Cancel</button>
            <button data-testid="product-form-save" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save product
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
